import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { SearchApi } from '@backstage/plugin-search-react';
import { SearchQuery, SearchResultSet } from '@backstage/plugin-search-common';
import qs from 'qs';
import { SEARCH_TYPES } from '../utils/search.constant';

export class SearchClient implements SearchApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /** The Confluence shard types the search backend currently serves */
  async fetchConfluenceShards(): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('search');
    const response = await this.fetchApi.fetch(`${baseUrl}/confluence/types`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data = await response.json();
    return data.types || [];
  }

  async query(query: SearchQuery): Promise<SearchResultSet> {
    const queryClone: SearchQuery = { ...query };
    if (queryClone.term) {
      queryClone.term = queryClone.term.replace(/^https?:\/\//i, '');
    }

    if (queryClone.types?.includes(SEARCH_TYPES.CONFLUENCE)) {
      queryClone.types = queryClone.types.filter(
        t => t !== SEARCH_TYPES.CONFLUENCE,
      );
      const shardTypes = await this.fetchConfluenceShards();
      if (shardTypes.length === 0) {
        return { results: [] };
      }
      queryClone.types.push(...shardTypes);
    }
    const queryString = qs.stringify(queryClone);
    const baseUrl = await this.discoveryApi.getBaseUrl('search');
    const url = `${baseUrl}/query?${queryString}`;
    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const result: SearchResultSet = await response.json();
    result.results =
      result.results?.map(r => ({
        ...r,
        type: r.type?.includes(SEARCH_TYPES.CONFLUENCE)
          ? SEARCH_TYPES.CONFLUENCE
          : r.type,
      })) || [];

    return result;
  }
}
